import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Plus, Trash2, Edit, Sparkles, Loader2, Eye, ExternalLink } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [editProduct, setEditProduct] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, [search, category, page]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // Convert object to array
      const catsData = response.data.categories;
      if (Array.isArray(catsData)) {
        setCategories(catsData);
      } else if (typeof catsData === 'object') {
        setCategories(Object.keys(catsData));
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '10' });
      if (search) params.append('search', search);
      if (category && category !== 'all') params.append('category', category);
      
      const response = await axios.get(`${API}/admin/products?${params}`);
      setProducts(response.data.products);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Errore nel caricamento prodotti');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Prodotto eliminato');
      fetchProducts();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleUpdate = async () => {
    if (!editProduct) return;
    
    try {
      await axios.put(`${API}/admin/products/${editProduct.id}`, {
        is_available: editProduct.is_available,
        is_featured: editProduct.is_featured,
        is_popular: editProduct.is_popular,
        price_override: editProduct.price_override || null
      });
      toast.success('Prodotto aggiornato');
      setEditDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  const handleGenerateAI = async (productId, language = 'it', regenerateImages = false) => {
    setGenerating(productId);
    try {
      const response = await axios.post(`${API}/admin/products/${productId}/generate-ai`, {
        product_id: productId,
        language,
        regenerate_images: regenerateImages
      });
      toast.success('Contenuto AI generato', {
        description: response.data.title
      });
      fetchProducts();
    } catch (error) {
      toast.error('Errore nella generazione AI');
    } finally {
      setGenerating(null);
    }
  };

  const placeholderImage = 'https://images.unsplash.com/photo-1592868378269-2c502b2730ff?w=100&h=100&fit=crop';

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-serif">Prodotti</h1>
          <p className="text-muted-foreground mt-1">Gestisci il catalogo prodotti</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca prodotti..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10"
            data-testid="search-products"
          />
        </div>
        <Select value={category} onValueChange={(val) => { setCategory(val); setPage(1); }}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 font-medium">Prodotto</th>
                <th className="text-left p-4 font-medium">Categoria</th>
                <th className="text-left p-4 font-medium">Costo</th>
                <th className="text-left p-4 font-medium">Prezzo</th>
                <th className="text-left p-4 font-medium">Margine</th>
                <th className="text-left p-4 font-medium">Stock</th>
                <th className="text-left p-4 font-medium">Stato</th>
                <th className="text-left p-4 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-t border-border">
                    <td colSpan={8} className="p-4">
                      <div className="h-16 bg-muted rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-muted-foreground">
                    Nessun prodotto trovato. Importa prodotti dalla sezione "Importa".
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="border-t border-border hover:bg-muted/20" data-testid={`product-row-${product.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted shrink-0">
                          <img
                            src={product.ai_images?.[0] || product.original_images?.[0] || placeholderImage}
                            alt={product.original_title}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.src = placeholderImage; }}
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate max-w-[200px]">
                            {product.ai_title?.it || product.original_title}
                          </p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            ID: {product.id.slice(0, 8)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">{product.category}</Badge>
                    </td>
                    <td className="p-4 font-mono text-sm">
                      €{product.supplier_cost?.toFixed(2)}
                    </td>
                    <td className="p-4 font-mono text-sm font-bold">
                      €{product.prices?.selling_price?.toFixed(2)}
                    </td>
                    <td className="p-4">
                      <Badge variant={product.prices?.margin_percent >= 20 ? 'default' : 'secondary'}>
                        {product.prices?.margin_percent?.toFixed(0)}%
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className={product.stock_quantity > 0 ? 'text-green-600' : 'text-red-600'}>
                        {product.stock_quantity}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        {product.is_available && <Badge className="bg-green-100 text-green-700">Attivo</Badge>}
                        {product.is_featured && <Badge className="bg-amber-100 text-amber-700">Featured</Badge>}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setEditProduct(product); setEditDialogOpen(true); }}
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleGenerateAI(product.id, 'it', false)}
                          disabled={generating === product.id}
                          data-testid={`generate-ai-${product.id}`}
                        >
                          {generating === product.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Precedente
            </Button>
            <span className="flex items-center px-4 text-sm text-muted-foreground">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Successivo
            </Button>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Prodotto</DialogTitle>
          </DialogHeader>
          {editProduct && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <img
                  src={editProduct.ai_images?.[0] || editProduct.original_images?.[0] || placeholderImage}
                  alt={editProduct.original_title}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div>
                  <p className="font-medium">{editProduct.ai_title?.it || editProduct.original_title}</p>
                  <p className="text-sm text-muted-foreground">{editProduct.category}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Override Prezzo (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={`Min: €${editProduct.prices?.min_price?.toFixed(2)}`}
                  value={editProduct.price_override || ''}
                  onChange={(e) => setEditProduct(prev => ({
                    ...prev,
                    price_override: e.target.value ? parseFloat(e.target.value) : null
                  }))}
                />
                <p className="text-xs text-muted-foreground">
                  Prezzo minimo: €{editProduct.prices?.min_price?.toFixed(2)} | 
                  Target: €{editProduct.prices?.target_price?.toFixed(2)}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <Label>Prodotto Attivo</Label>
                <Switch
                  checked={editProduct.is_available}
                  onCheckedChange={(val) => setEditProduct(prev => ({ ...prev, is_available: val }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>In Evidenza (Featured)</Label>
                <Switch
                  checked={editProduct.is_featured}
                  onCheckedChange={(val) => setEditProduct(prev => ({ ...prev, is_featured: val }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Prodotto Popolare (prezzo target)</Label>
                <Switch
                  checked={editProduct.is_popular}
                  onCheckedChange={(val) => setEditProduct(prev => ({ ...prev, is_popular: val }))}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleGenerateAI(editProduct.id, 'it', true)}
                  disabled={generating === editProduct.id}
                >
                  {generating === editProduct.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Genera AI + Immagine
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleUpdate}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
